import { useContext } from 'react';
import clsx from 'clsx';
import { isEmpty, keyBy } from 'lodash-es';
import { AText, Box, copyTextToClipboard } from '@alf/uikit';
import { SENDER_CHAT, SupportChat, useOpenChat } from '@namespace/chats';
import { SiteSettingsContext, useOtherSettings } from '@namespace/cms';
import { TextCopy } from '@namespace/common';
import { useI18n } from '@namespace/i18n';
import { useNotifications } from '@namespace/notifications';
import { useUserSegmentsIncludes } from '@namespace/segments';
import { useGetImage } from '@namespace/themes';
import CustomChatButton from './CustomChatButton';

import styles from './index.module.css';

const MAILTO_PREFIX = 'mailto:';

const BottomPopup = ({ bottomPopupItems, onClosePopup, classNames = {} }) => {
  const { t, language } = useI18n();
  const getImg = useGetImage();
  const [siteSettings] = useContext(SiteSettingsContext);
  const { siteConfig = [] } = siteSettings;
  const { supportChatName = SENDER_CHAT } = siteConfig;
  const { openChat } = useOpenChat(supportChatName);
  const { isCopyEmailSupportButtonVisible } = useOtherSettings();
  const filteredSegments = useUserSegmentsIncludes();
  const { customNotification } = useNotifications();

  const onChatOpen = (e) => {
    e.stopPropagation();
    openChat();
  };

  const normalizedPopupItems = bottomPopupItems.map((item) => {
    let el = { ...item, onClick: (e) => e.stopPropagation() };
    if (!isEmpty(item.langLinks)) {
      const langLinks = keyBy(item.langLinks, 'lang');
      el = {
        ...item,
        action:
          langLinks[language]?.action || langLinks?.en?.action || item?.action
      };
    } else if (item.action === 'openChat') {
      el = {
        ...item,
        action: null,
        onClick: onChatOpen
      };
    } else if (item.action === 'copyEmail') {
      el = {
        ...item,
        isShowCopyIcon: true,
        action: null,
        onClick: async (e) => {
          e.stopPropagation();
          await copyTextToClipboard(t(item?.title));

          customNotification({
            notificationType: 'info',
            message: t('uikit.textCopy.notification', ['E-mail']),
            timeout: 3000
          });
        }
      };
    }
    return el;
  });

  return (
    <Box className={styles.bottomPopupWrapper} direction="column">
      <Box
        className={clsx(
          styles.bottomPopupOverlay,
          classNames?.bottomPopupOverlay
        )}
        onClick={onClosePopup}
      />
      <Box className={styles.bottomPopupMenu} direction="column">
        {normalizedPopupItems.map(
          ({
            title,
            image,
            imageDark,
            action,
            userSegments,
            onClick,
            isShowCopyIcon
          }) => {
            const isItemIncludedInSegments = filteredSegments(userSegments);
            const isCustomChat = action === 'openCustomChat';
            const emailAddressItem =
              (action && action.split(MAILTO_PREFIX)[1]) || false;

            return (
              isItemIncludedInSegments &&
              (isCustomChat ? (
                <SupportChat
                  ChatButton={CustomChatButton}
                  title={title}
                  image={image}
                  imageDark={imageDark}
                  key={title}
                />
              ) : (
                <Box>
                  <a
                    className={styles.bottomPopupItem}
                    target="_blank"
                    onClick={onClick}
                    href={action}
                    rel="noreferrer"
                    key={title}
                  >
                    <Box align="center">
                      {isShowCopyIcon && (
                        <AText
                          className={styles.copyText}
                          color="po_txt_color_1"
                          breed="B"
                          size="m"
                        >
                          {t('content.usermenu.support.copyText')}
                        </AText>
                      )}
                      {!isShowCopyIcon && (
                        <img
                          src={getImg(image, imageDark)}
                          className={styles.bottomPopupIcon}
                          alt=""
                        />
                      )}
                      <AText breed="R" size="m" color="po_menutxt_color_1">
                        {t(title)}
                      </AText>
                      {isShowCopyIcon && (
                        <TextCopy
                          classNames={{ copyTextWrapper: styles.copyTextIcon }}
                          breed="R"
                          size="s"
                          color="po_txt_color_3"
                          textCopy={t(title)}
                          textNotify="E-mail"
                        />
                      )}
                    </Box>
                  </a>
                  {emailAddressItem && isCopyEmailSupportButtonVisible && (
                    <TextCopy
                      iconSize="l"
                      classNames={{
                        copyTextWrapper: clsx([
                          styles.bottomPopupItem,
                          styles.emailPopupItem
                        ])
                      }}
                      gap="xxs"
                      color="input_txt_placeholder"
                      textCopy={emailAddressItem}
                      textNotify="Email"
                      dataRoles={{
                        dataRoleText: 'bet-text-copy-text',
                        dataRoleIcon: 'bet-text-copy-icon'
                      }}
                    />
                  )}
                </Box>
              ))
            );
          }
        )}
      </Box>
    </Box>
  );
};

export default BottomPopup;
